import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Hub } from 'aws-amplify';
import { useAppDispatch } from '../app/hooks';
import { fetchUserDataByToken } from '../app/store/userSlice';
import { MAX_SESSION_LENGTH, persistSignInSession } from '../services/auth';
import useLogout from './useLogout';

// Public entry points that don't require a prior session - visiting these while logged out shouldn't force a redirect.
export const PUBLIC_ROUTES = ['/', '/sign-up', '/admin-sso-login'];

// OAuth callback landed but Amplify hasn't finished the token exchange - no session data in localStorage yet.
export const isOAuthCallbackInProgress = () => {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') || params.has('error');
};

// App-wide session keeper: populates `user` after any sign-in and logs out expired or missing sessions.
export default function useGlobalAuthSession() {
  const dispatch = useAppDispatch();
  const handleLogout = useLogout();
  const location = useLocation();

  useEffect(() => {
    const listener = async (data: any) => {
      const event = data.payload.event;
      if (event === 'signIn' || event === 'cognitoHostedUI') {
        // Robustly persists fixedLoginTime + amplifyTokenExpiry (never throws) so a
        // failed token refresh can't strand session validation and force a logout.
        await persistSignInSession();
        dispatch(fetchUserDataByToken());
      }
    };

    Hub.listen('auth', listener);

    return () => {
      Hub.remove('auth', listener);
    };
  }, [dispatch]);

  // Enforces token expiry (amplifyTokenExpiry) and the 24h max session (fixedLoginTime); logs out on failure.
  useEffect(() => {
    const validateSession = () => {
      if (PUBLIC_ROUTES.includes(location.pathname) || isOAuthCallbackInProgress()) return;

      try {
        const tokenExpiryStr = localStorage.getItem('amplifyTokenExpiry');
        const loginTimeStr = localStorage.getItem('fixedLoginTime');

        if (!tokenExpiryStr || !loginTimeStr) {
          throw new Error('Missing session data in localStorage');
        }

        const tokenExpiry = parseInt(tokenExpiryStr, 10);
        const loginTime = parseInt(loginTimeStr, 10);

        if (isNaN(tokenExpiry) || isNaN(loginTime)) {
          throw new Error('Invalid token or login time format');
        }

        const now = Date.now();
        const sessionExpiresAt = loginTime + MAX_SESSION_LENGTH;

        if (now > sessionExpiresAt) {
          throw new Error('Session expired due to MAX_SESSION_LENGTH');
        }

        if (now > tokenExpiry) {
          throw new Error('Token has expired');
        }
      } catch (err) {
        console.error('Session validation failed:', err);
        handleLogout();
      }
    };

    validateSession();
  }, [dispatch]);
}
